import {
  Container,
  LoaderCircle,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import CajaRejillaModal from '../components/bodega/CajaRejillaModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { formatearKilosPos } from '../utils/pos'

function CajasRejillas() {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const esAdministrador = empresaActual?.rol === 'administrador'

  const [tiposEnvase, setTiposEnvase] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalAbierto, setModalAbierto] = useState(false)
  const [tipoEnvaseEditar, setTipoEnvaseEditar] = useState(null)
  const [actualizandoId, setActualizandoId] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setTiposEnvase([])
      setCargando(false)
      return
    }

    cargarTiposEnvase()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarTiposEnvase = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('tipos_envase')
        .select('*')
        .eq('empresa_id', empresaActual.id)
        .order('nombre')

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setTiposEnvase(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar cajas/rejillas:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar las cajas/rejillas.'
      )
      setTiposEnvase([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const tiposEnvaseFiltrados = tiposEnvase.filter((tipoEnvase) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    return (tipoEnvase.nombre || '')
      .toLowerCase()
      .includes(termino)
  })

  const abrirCreacion = () => {
    setTipoEnvaseEditar(null)
    setModalAbierto(true)
  }

  const abrirEdicion = (tipoEnvase) => {
    setTipoEnvaseEditar(tipoEnvase)
    setModalAbierto(true)
  }

  const cerrarModalTipoEnvase = () => {
    setModalAbierto(false)
    setTipoEnvaseEditar(null)
  }

  const alGuardar = (tipoEnvaseGuardado) => {
    const fueEdicion = Boolean(tipoEnvaseEditar)

    cerrarModalTipoEnvase()
    cargarTiposEnvase()

    abrirModal({
      tipo: 'success',
      titulo: fueEdicion
        ? 'Caja/rejilla actualizada'
        : 'Caja/rejilla creada',
      mensaje: `La caja/rejilla "${tipoEnvaseGuardado?.nombre}" fue ${
        fueEdicion ? 'actualizada' : 'creada'
      } correctamente.`,
    })
  }

  const aplicarCambioEstado = async (
    tipoEnvase,
    activar,
    esDesactivacion
  ) => {
    if (esDesactivacion) {
      actualizarModal({
        procesando: true,
        mostrarCancelar: false,
        textoAceptar: 'Desactivando...',
      })
    }

    try {
      setActualizandoId(tipoEnvase.id)

      const { error: errorActualizar } = await supabase
        .from('tipos_envase')
        .update({ activo: activar })
        .eq('id', tipoEnvase.id)
        .eq('empresa_id', empresaActual.id)

      if (errorActualizar) {
        throw errorActualizar
      }

      if (!montadoRef.current) return

      await cargarTiposEnvase()

      if (esDesactivacion) {
        abrirModal({
          tipo: 'success',
          titulo: 'Caja/rejilla desactivada',
          mensaje: 'La caja/rejilla fue desactivada correctamente.',
        })
      }
    } catch (errorEstado) {
      console.error(
        'Error al actualizar estado de la caja/rejilla:',
        errorEstado
      )

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar',
        mensaje:
          errorEstado.message ||
          'No fue posible actualizar el estado de la caja/rejilla.',
      })
    } finally {
      if (montadoRef.current) {
        setActualizandoId(null)
      }
    }
  }

  const cambiarEstado = (tipoEnvase) => {
    const activar = !tipoEnvase.activo

    if (!activar) {
      abrirModal({
        tipo: 'confirm',
        titulo: 'Desactivar caja/rejilla',
        mensaje: `¿Deseas desactivar la caja/rejilla "${tipoEnvase.nombre}"?`,
        textoAceptar: 'Desactivar',
        textoCancelar: 'Cancelar',
        mostrarCancelar: true,
        onAceptar: () => aplicarCambioEstado(tipoEnvase, false, true),
      })
      return
    }

    aplicarCambioEstado(tipoEnvase, true, false)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando cajas/rejillas...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarTiposEnvase}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (tiposEnvase.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Container
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay cajas/rejillas registradas
          </h3>

          <p className="mt-2 text-slate-500">
            {esAdministrador
              ? 'Presiona “Nueva caja/rejilla” para ingresar el primer registro.'
              : 'Aún no se han registrado cajas/rejillas.'}
          </p>

          {esAdministrador && (
            <button
              type="button"
              onClick={abrirCreacion}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <Plus size={19} />
              Ingresar primera caja/rejilla
            </button>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por nombre"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {tiposEnvaseFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron cajas/rejillas para “{busqueda}”.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile: tarjetas apiladas (evita tabla ancha con scroll horizontal). */}
            <div className="space-y-3 md:hidden">
              {tiposEnvaseFiltrados.map((tipoEnvase) => (
                <div
                  key={tipoEnvase.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 font-semibold text-slate-900">
                      {tipoEnvase.nombre}
                    </p>

                    {tipoEnvase.activo ? (
                      <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                        Activo
                      </span>
                    ) : (
                      <span className="shrink-0 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                        Inactivo
                      </span>
                    )}
                  </div>

                  <p className="mt-3 text-sm text-slate-600">
                    Tara:{' '}
                    <span className="font-semibold text-slate-900">
                      {formatearKilosPos(tipoEnvase.tara_kg)}
                    </span>
                  </p>

                  {esAdministrador && (
                    <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
                      <button
                        type="button"
                        onClick={() => abrirEdicion(tipoEnvase)}
                        title="Editar"
                        aria-label="Editar caja/rejilla"
                        className="rounded-lg border border-slate-300 p-3 text-slate-600 transition hover:bg-slate-100"
                      >
                        <Pencil size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => cambiarEstado(tipoEnvase)}
                        disabled={actualizandoId === tipoEnvase.id}
                        title={
                          tipoEnvase.activo ? 'Desactivar' : 'Reactivar'
                        }
                        aria-label={
                          tipoEnvase.activo
                            ? 'Desactivar caja/rejilla'
                            : 'Reactivar caja/rejilla'
                        }
                        className={
                          tipoEnvase.activo
                            ? 'rounded-lg border border-red-200 p-3 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
                            : 'rounded-lg border border-emerald-200 p-3 text-emerald-600 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                        }
                      >
                        {actualizandoId === tipoEnvase.id ? (
                          <LoaderCircle
                            size={18}
                            className="animate-spin"
                          />
                        ) : tipoEnvase.activo ? (
                          <PowerOff size={18} />
                        ) : (
                          <Power size={18} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tablet / desktop: tabla completa. */}
            <div className="hidden w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
              <table className="w-full text-left text-sm md:min-w-[520px]">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-6 py-3">Nombre</th>
                    <th className="px-6 py-3">Tara</th>
                    <th className="px-6 py-3">Estado</th>
                    {esAdministrador && (
                      <th className="px-6 py-3 text-right">
                        Acciones
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {tiposEnvaseFiltrados.map((tipoEnvase) => (
                    <tr key={tipoEnvase.id}>
                      <td className="px-6 py-4 font-semibold text-slate-900">
                        {tipoEnvase.nombre}
                      </td>

                      <td className="px-6 py-4 text-slate-700">
                        {formatearKilosPos(tipoEnvase.tara_kg)}
                      </td>

                      <td className="px-6 py-4">
                        {tipoEnvase.activo ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                            Inactivo
                          </span>
                        )}
                      </td>

                      {esAdministrador && (
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => abrirEdicion(tipoEnvase)}
                              title="Editar"
                              aria-label="Editar caja/rejilla"
                              className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                            >
                              <Pencil size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() => cambiarEstado(tipoEnvase)}
                              disabled={
                                actualizandoId === tipoEnvase.id
                              }
                              title={
                                tipoEnvase.activo
                                  ? 'Desactivar'
                                  : 'Reactivar'
                              }
                              aria-label={
                                tipoEnvase.activo
                                  ? 'Desactivar caja/rejilla'
                                  : 'Reactivar caja/rejilla'
                              }
                              className={
                                tipoEnvase.activo
                                  ? 'rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
                                  : 'rounded-lg border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                              }
                            >
                              {actualizandoId === tipoEnvase.id ? (
                                <LoaderCircle
                                  size={16}
                                  className="animate-spin"
                                />
                              ) : tipoEnvase.activo ? (
                                <PowerOff size={16} />
                              ) : (
                                <Power size={16} />
                              )}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Mantenedores
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Cajas/Rejillas
          </h2>

          <p className="mt-2 text-slate-500">
            Administra las cajas/rejillas de la empresa seleccionada.
          </p>
        </div>

        {esAdministrador && (
          <button
            type="button"
            onClick={abrirCreacion}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nueva caja/rejilla
          </button>
        )}
      </div>

      {renderContenido()}

      {modalAbierto && (
        <CajaRejillaModal
          tipoEnvase={tipoEnvaseEditar}
          onClose={cerrarModalTipoEnvase}
          onGuardado={alGuardar}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default CajasRejillas
